/**
 * What a domain gives the kiosk: its strings, brand, widgets, and what the backend's spec cannot say by itself — the
 * builders, menus and actions its screens name by role. What each screen shows comes from the spec (`GET /api/v2/ui`,
 * spec-screen.ts); `screens` keeps only what the spec does not describe. The platform renders it; it never knows
 * what a train or a withdrawal is.
 *
 * To add a domain: a folder in src/domains/<name>/ exporting a DomainPack, listed in src/app/packs.ts.
 */
import type { StageWish } from '../stage/catalog';
import type { LayoutChange } from '../layout/changes';
import type { Preset } from '../layout/model';
import type { Catalogs, Translate } from '../i18n/translator';
import type { AssistantMessage, Session } from '../core/wire';
import type { WidgetDefinition } from '../widgets/contract';

/** What a screen definition can read and do. The only API a pack sees. */
export interface ScreenContext {
  session: Session;
  language: string;
  t: Translate;
  /** What the user is filling in on this screen and has not sent yet (counters, picked seats). */
  draft: Readonly<Record<string, unknown>>;
  /** True if the backend allows this action now (`session.valid_actions`). */
  can(action: string): boolean;
}

/** One widget with its props: what the content area shows. */
export interface WidgetCall {
  widget: string;
  props: unknown;
  /** The look the pack suggests for this screen; Settings and the preset can choose another. */
  variant?: string;
}

/** A widget shown over the content because a reply asked for it; it closes by itself after `closeAfterMs`. */
export interface OverlayCall extends WidgetCall {
  closeAfterMs?: number;
}

export type ActionTone = 'primary' | 'neutral' | 'quiet';

/**
 * A main-bar action. It sends a button (`press`) or words (`say`, an i18n key) — words for the things
 * the domain's spec has no button for. It is shown only if the backend allows `gate` (default: the
 * button). Data the user prepared on screen comes from `ctx.draft` when the pack builds the action.
 */
export interface ActionDef {
  id: string;
  label: string;
  icon?: string;
  press?: { button: string; data?: Record<string, unknown> };
  say?: string;
  gate?: string | null;
  tone?: ActionTone;
  /** Where in the bar: `end` puts it on the right, where "next" lives. */
  place?: 'start' | 'end';
  /** A tap opens this menu in place of the bar's row, instead of pressing or saying (packs/menu.ts). */
  menu?: MenuDef;
}

/**
 * A main-bar menu (the old kiosk's sub-carousels): a row of chips that replaces the bar's actions,
 * "← Back" first. The pack builds the chips of each view from what was picked so far; the platform
 * draws them, keeps the state and goes back. It closes when it does something, or the screen changes.
 */
export interface MenuDef {
  /** The view it opens on (default `main`). */
  start?: string;
  view(state: MenuState, ctx: ScreenContext): MenuEntry[];
}

export interface MenuState {
  view: string;
  /** What the user picked in the menu so far (a station, a day…). */
  values: Readonly<Record<string, string>>;
}

/** What a chip does: open another view, keep values (and maybe open a view), or act and close. */
export type MenuStep =
  | { open: string }
  | { set: Record<string, string>; open?: string }
  | { press: { button: string; data?: Record<string, unknown> } }
  | { say: string };

export interface MenuChip {
  id: string;
  /** Already in the user's language (the pack builds the view with ctx.t). */
  label: string;
  icon?: string;
  /** An emoji instead of an icon (the old kiosk's cities, FAQ topics). */
  emoji?: string;
  selected?: boolean;
  disabled?: boolean;
  /** `main`: the menu's main action, filled with the accent. */
  tone?: 'main';
  step: MenuStep;
}

export type MenuEntry = MenuChip | { divider: true };

export interface ScreenDef {
  view(ctx: ScreenContext): WidgetCall | null;
  actions?(ctx: ScreenContext): ActionDef[];
  layout?: LayoutChange[];
  /** i18n key of the status-line hint. */
  hint?: string;
  /** The progress step this screen belongs to; STEP_DONE: every step behind (the task is over). */
  step?: string;
  /** The widgets it may show, without building it (its `widget:`, its builder's, each of its cases'): Settings' Screens tab. */
  widgets?: readonly string[];
}

/** The step of a screen that ends the task: the progress shows every step done (backend `UI_STEP_DONE`). */
export const STEP_DONE = 'done';

/**
 * What a pack computes for a screen the spec describes with `build:` (spec-screen.ts): the widget and its props from
 * the session, with the spec's own props already resolved. Named by role (`trip_summary`), never by a screen.
 */
export type ScreenBuilder = ((ctx: ScreenContext, props: Record<string, unknown>) => WidgetCall | null) & {
  /** The widget it gives (docs/NEW-UI/07 D-05): what Settings offers a look of, before the screen is built. */
  readonly widget?: string;
};

/** A builder that says which widget it gives. */
export function defineBuilder(widget: string, build: (ctx: ScreenContext, props: Record<string, unknown>) => WidgetCall | null): ScreenBuilder {
  return Object.assign(build, { widget });
}

export interface ProgressStep {
  id: string;
  label: string;
}

/** The waiting video of a pack (DomainPack.attract). */
export interface AttractVideoDef {
  video: string;
  trimEndS?: number;
}

export interface DomainPack {
  /** The backend's name for the domain (`GET /api/runtime` → domain). */
  id: string;
  /** For recordings older than `run.domain`: does this session belong to this domain? */
  recognizes(session: Session): boolean;
  /** The brand: `--brand-accent` and `--brand-accent-strong` (tokens.css). The theme turns them into the
   *  accent colours for its scheme; surfaces belong to the theme. */
  theme?: Record<string, string>;
  /**
   * What the kiosk plays while it waits for someone, in the content area: at the start screen, and
   * before there is a session (a replay at its first step). A looping video, muted; `trimEndS` seconds
   * at its end are skipped (an ending that does not fit the frame).
   */
  attract?: AttractVideoDef;
  /** The avatar and the background it wants, by name in the build's catalog (stage/catalog.ts). */
  stage?: StageWish;
  /** For the spec's `build:` names: computed widgets, by role. */
  builders?: Record<string, ScreenBuilder>;
  /** For the spec's `{menu: name}` in a bar. */
  menus?: Record<string, ActionDef>;
  /** For the spec's `{action: name}` in a bar: an action built from the screen (its data from the draft). */
  actions?: Record<string, (ctx: ScreenContext) => ActionDef>;
  /**
   * The domain's UI document as the backend writes it (tools/ui_document.py → `ui.json`, kept current by a test):
   * what a recording older than the spec's `ui` is shown with, and what the pack's tests check every screen against.
   */
  ui?: unknown;
  widgets?: WidgetDefinition<never>[];
  layout?: LayoutChange[];
  presets?: Preset[];
  defaultPreset?: string;
  /** The button that records the accessibility mode on the backend, if the domain has one (`{mode}`). */
  accessibilityButton?: string;
  /** Something a reply asks to show on top of the content (the trains' station map). */
  overlayFor?(message: AssistantMessage, ctx: ScreenContext): OverlayCall | null;
}

/**
 * The pack the kiosk runs (spec-screen.ts `withSpec`): a pack says nothing of its screens nor writes a word; the
 * screens, the progress steps and the words are its spec's (docs/NEW-UI/05-spec-ui.md).
 */
export interface KioskPack extends DomainPack {
  screens: Record<string, ScreenDef>;
  steps: ProgressStep[];
  /** The domain's words, the spec's too: a pack writes none. */
  strings: Catalogs;
  /** The languages both the platform and the domain write, in the platform's order. */
  languages: string[];
  /** The spec's touch buttons: the id a touch sends → the action it runs. */
  buttons: Record<string, string>;
}
